import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { logoutUser } from '../store/slices/authSlice';
import { toast } from 'react-toastify';
import './AdminLayout.css';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchBusinessAccounts, setCurrentAccount } from '../store/slices/businessAccountsSlice';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useSelector(state => state.auth);
  const data = useSelector(state => state.auth);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  console.log('Current user:', data);
  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      toast.success('Logged out successfully');
      navigate('/signin');
    } catch (err) {
      toast.error('Logout failed');
      // Force logout anyway
      navigate('/signin');
    }
  };

  const menuItems = [
    { path: '/dashboard', icon: '📊', label: 'Dashboard' },
    { path: '/conversations', icon: '💬', label: 'Conversations' },
    { path: '/customers', icon: '👥', label: 'Customers' },
    { path: '/leads', icon: '📋', label: 'Leads' },
    { path: '/appointments', icon: '📅', label: 'Appointments' },
    { path: '/catalog', icon: '📦', label: 'Catalog' },
    // { path: '/promotions', icon: '🎯', label: 'Promotions' },
    { path: '/broadcast', icon: '📢', label: 'Broadcast' },
    { path: '/broadcast-lists', icon: '📋', label: 'Broadcast Lists' },
    { path: '/feedback', icon: '⭐', label: 'Feedback' },
    { path: '/analytics', icon: '📈', label: 'Analytics' },
    { path: '/templates', icon: '📝', label: 'Templates' },
    { path: '/template-settings', icon: '⚙️', label: 'Template Settings' },
    { path: '/invoices', icon: '🧾', label: 'Invoices' },
    { path: '/orders', icon: '🛒', label: 'Orders' },
    // Technical/Admin modules hidden from business user view
     { path: '/phone-numbers', icon: '📱', label: 'Phone Numbers' },
    // { path: '/webhooks', icon: '🔗', label: 'Webhooks' },
    { path: '/media', icon: '🖼️', label: 'Media' },
    { path: '/business-accounts', icon: '🏢', label: 'Business Accounts' },
    { path: '/integrations/token', icon: '🔑', label: 'Integrations' },
    // { path: '/quality-rating', icon: '⭐', label: 'Quality Rating' },
    // { path: '/api-logs', icon: '📋', label: 'API Logs' },
    { path: '/billing', icon: '💳', label: 'Billing' },
    { path: '/settings', icon: '⚙️', label: 'Settings' }
  ];

  const { accounts, currentAccount } = useAppSelector(state => ({
    accounts: state.businessAccounts.accounts || [],
    currentAccount: state.businessAccounts.currentAccount || null
  }));

  useEffect(() => {
    dispatch(fetchBusinessAccounts());
  }, [dispatch]);

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="admin-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <div className="logo-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </div>
            {sidebarOpen && <span className="logo-text">WhatsApp Business</span>}
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {sidebarOpen ? (
                <polyline points="18 6 12 12 6 6"></polyline>
              ) : (
                <polyline points="6 9 12 15 18 9"></polyline>
              )}
            </svg>
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              {sidebarOpen && <span className="nav-label">{item.label}</span>}
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">👤</div>
            {sidebarOpen && (
              <div className="user-info">
                <div className="user-name">{user?.businessName || 'User'}</div>
                <div className="user-email">{user?.email || 'email@example.com'}</div>
              </div>
            )}
          </div>
          <button 
            className="logout-btn"
            onClick={handleLogout}
            title="Logout"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </button>
        </div>
      </aside>

      <div className="admin-content">
        <header className="admin-header">
          <div className="header-left">
            <h1 className="page-title">
              {location.pathname === '/dashboard' && 'Dashboard'}
              {location.pathname.startsWith('/conversations') && 'Conversations'}
              {location.pathname === '/customers' && 'Customers'}
              {location.pathname.startsWith('/customers/') && location.pathname.includes('/chat') && 'Chat'}
              {location.pathname === '/leads' && 'Leads'}
              {location.pathname === '/appointments' && 'Appointments'}
              {location.pathname === '/catalog' && 'Catalog'}
              {location.pathname === '/promotions' && 'Promotions'}
              {location.pathname === '/broadcast' && 'Broadcast'}
              {location.pathname === '/broadcast-lists' && 'Broadcast Lists'}
              {location.pathname === '/analytics' && 'Analytics'}
              {location.pathname === '/templates' && 'Templates'}
              {location.pathname === '/template-settings' && 'Template Settings'}
              {location.pathname === '/invoices' && 'Invoices'}
              {location.pathname.startsWith('/orders') && 'Orders'}
              {location.pathname === '/phone-numbers' && 'Phone Numbers'}
              {location.pathname === '/webhooks' && 'Webhooks'}
              {location.pathname === '/business-accounts' && 'Business Accounts'}
              {location.pathname === '/integrations/token' && 'Integration API Key'}
              {location.pathname === '/quality-rating' && 'Quality Rating'}
              {location.pathname === '/api-logs' && 'API Logs'}
              {location.pathname === '/billing' && 'Billing'}
              {location.pathname === '/settings' && 'Settings'}
            </h1>
          </div>
          <div className="header-right">
            

            <button className="header-icon-btn" title="Notifications">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              <span className="notification-badge">3</span>
            </button>
            <button className="header-icon-btn" title="Settings">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"></path>
              </svg>
            </button>
          </div>
        </header>
        <main className="admin-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;

